import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Utensils, Stethoscope, TrendingUp, CreditCard, UserCheck, Calendar, Phone, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import TestOptions from "@/components/onboarding/TestOptions";
import RegistrationPrompt from "@/components/onboarding/RegistrationPrompt";
import { ConversionTracking, trackConversion } from "@/components/tracking/ConversionTracking";
import SocialShareButtons from "@/components/SocialShareButtons";
import { SharedHeader } from "@/components/SharedHeader";

interface Template {
  id: string;
  name: string;
  icon: typeof Utensils;
  description: string;
  features: string[];
  config: {
    callType: string;
    purpose: string;
    tone: string;
    features: string[];
    systemPrompt: string;
  };
}

const templates: Template[] = [
  {
    id: 'restaurant',
    name: 'Restaurant',
    icon: Utensils,
    description: 'Perfect for taking reservations, managing waitlists, and answering customer questions',
    features: ['Take reservations 24/7', 'Manage waitlist', 'Answer menu questions', 'Handle special requests'],
    config: {
      callType: 'incoming',
      purpose: 'reservations',
      tone: 'friendly',
      features: ['appointments', 'information', 'transfer'],
      systemPrompt: 'You are a friendly restaurant host helping with reservations, waitlist management, and answering questions about the menu and special events.'
    }
  },
  {
    id: 'medical',
    name: 'Medical Office',
    icon: Stethoscope,
    description: 'Ideal for appointment scheduling, reminders, and patient information collection',
    features: ['Schedule appointments', 'Send reminders', 'Collect insurance info', 'HIPAA-compliant responses'],
    config: {
      callType: 'both',
      purpose: 'appointments',
      tone: 'professional',
      features: ['appointments', 'reminders', 'information', 'transfer'],
      systemPrompt: 'You are a professional medical office assistant helping with appointment scheduling, sending reminders, and collecting patient information. Maintain patient privacy and professionalism.'
    }
  },
  {
    id: 'sales',
    name: 'Sales Team',
    icon: TrendingUp,
    description: 'Optimized for lead qualification, follow-ups, and booking sales calls',
    features: ['Qualify leads', 'Schedule demos', 'Follow up automatically', 'CRM integration ready'],
    config: {
      callType: 'outgoing',
      purpose: 'sales',
      tone: 'professional',
      features: ['appointments', 'information', 'follow-ups'],
      systemPrompt: 'You are an enthusiastic sales assistant helping qualify leads, schedule product demos, and follow up with potential customers. Focus on understanding their needs and booking qualified meetings.'
    }
  },
  {
    id: 'payment',
    name: 'Payment Collection',
    icon: CreditCard,
    description: 'Specialized for collecting payments, processing orders, and handling billing inquiries',
    features: ['Collect payment details', 'Process orders securely', 'Answer billing questions', 'Send payment confirmations'],
    config: {
      callType: 'both',
      purpose: 'payment',
      tone: 'professional',
      features: ['payment', 'information', 'transfer'],
      systemPrompt: 'You are a professional payment specialist helping customers with payment collection, order processing, and billing inquiries. Ensure secure handling of payment information and provide clear confirmation of transactions.'
    }
  },
  {
    id: 'lead-qualification',
    name: 'Lead Qualification',
    icon: UserCheck,
    description: 'Designed to qualify and score leads before routing to sales team',
    features: ['Ask qualifying questions', 'Score lead quality', 'Gather contact info', 'Route to right team'],
    config: {
      callType: 'both',
      purpose: 'lead-qualification',
      tone: 'friendly',
      features: ['information', 'appointments', 'transfer'],
      systemPrompt: 'You are a lead qualification specialist helping identify potential customers, gather key information, and score leads based on their needs and fit. Ask targeted questions to understand budget, timeline, and decision-making authority.'
    }
  },
  {
    id: 'appointments',
    name: 'Appointment Scheduling',
    icon: Calendar,
    description: 'Focused on scheduling, rescheduling, and managing appointments efficiently',
    features: ['Schedule new appointments', 'Handle rescheduling', 'Send reminders', 'Check availability'],
    config: {
      callType: 'both',
      purpose: 'appointments',
      tone: 'friendly',
      features: ['appointments', 'reminders', 'information'],
      systemPrompt: 'You are an appointment scheduling specialist helping customers book, reschedule, and manage appointments. Check availability, send confirmations, and provide reminders to ensure smooth scheduling.'
    }
  }
];

type Step = 'select' | 'customize' | 'test' | 'register';

const TemplateToAgent = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  
  const [step, setStep] = useState<Step>('select');
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [agentConfig, setAgentConfig] = useState<any>(null);
  const [sessionId, setSessionId] = useState<string>('');
  const [agentId, setAgentId] = useState<string>('');
  
  // Customization form state
  const [businessName, setBusinessName] = useState('');
  const [agentName, setAgentName] = useState('');
  const [language, setLanguage] = useState('en');
  const [voiceGender, setVoiceGender] = useState('female');
  const [tone, setTone] = useState('friendly');
  const [additionalInstructions, setAdditionalInstructions] = useState('');

  const handleTemplateSelect = (template: Template) => {
    setSelectedTemplate(template);
    setTone(template.config.tone);
    setStep('customize');
  };

  const handleCustomize = () => {
    if (!selectedTemplate) return;
    
    const config = {
      ...selectedTemplate.config,
      name: agentName || `${selectedTemplate.name} Agent`,
      businessName,
      language,
      voiceGender,
      tone,
      additionalInstructions,
      systemPrompt: `${selectedTemplate.config.systemPrompt}${businessName ? ` You work for ${businessName}.` : ''}${additionalInstructions ? ` Additional instructions: ${additionalInstructions}` : ''}`
    };
    
    setAgentConfig(config);
    setStep('test');
  };

  return (
    <>
      <Helmet>
        <title>AI Receptionist Templates | AI Customer Service Automation | Call24x7</title>
        <meta name="description" content="Pre-built AI receptionist service templates for restaurants, medical offices, sales teams. AI customer service automation ready in minutes. Start free!" />
        <meta name="keywords" content="AI receptionist service, AI customer service automation, AI customer support platform, 24x7 AI answering service, AI call center software, AI tools for small business call handling, how to automate customer calls with AI" />
        <link rel="canonical" href="https://call24x7.ai/template-to-agent" />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Call24x7.AI" />
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://call24x7.ai/template-to-agent" />
        <meta property="og:title" content="AI Receptionist Templates | AI Customer Service Automation" />
        <meta property="og:description" content="Pre-built AI receptionist service templates for restaurants, medical offices, sales teams. AI customer service automation ready in minutes." />
        <meta property="og:site_name" content="Call24x7.AI" />
        
        {/* Structured Data - ItemList for Templates */}
        <script type="application/ld+json">
          {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "name": "AI Receptionist Service Templates",
          "description": "Pre-built AI customer service automation templates for various industries",
          "itemListElement": [
            {"@type": "ListItem", "position": 1, "name": "Restaurant AI Receptionist", "description": "AI answering service for reservations, waitlist management, and menu questions"},
            {"@type": "ListItem", "position": 2, "name": "Medical Office AI Receptionist", "description": "AI customer support platform for appointment scheduling and patient information"},
            {"@type": "ListItem", "position": 3, "name": "Sales Team AI Agent", "description": "AI call center software for lead qualification and demo scheduling"},
            {"@type": "ListItem", "position": 4, "name": "Payment Collection AI Agent", "description": "AI customer service automation for payment processing and billing inquiries"},
            {"@type": "ListItem", "position": 5, "name": "Lead Qualification AI Agent", "description": "AI tools for small business call handling and lead scoring"},
            {"@type": "ListItem", "position": 6, "name": "Appointment Scheduling AI Agent", "description": "24/7 AI answering service for booking and managing appointments"}
          ]
        })}
        </script>
        
        {/* Breadcrumb */}
        <script type="application/ld+json">
          {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [{
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://call24x7.ai"
          }, {
            "@type": "ListItem",
            "position": 2,
            "name": "AI Receptionist Templates",
            "item": "https://call24x7.ai/template-to-agent"
          }]
        })}
        </script>
      </Helmet>
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-secondary/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      <div className="relative z-10">
        <SharedHeader />
        
        <div className="container mx-auto px-4 pt-28 pb-8">

        {/* Step: Template Selection */}
        {step === 'select' && (
          <div className="max-w-6xl mx-auto animate-fade-in">
            <div className="text-center mb-8">
              <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
                Choose Your Template
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Select a pre-built template for your industry and customize it to fit your needs
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {templates.map((template) => (
                <Card
                  key={template.id}
                  className="bg-card border-border hover:border-primary/50 transition-all cursor-pointer group p-6"
                  onClick={() => handleTemplateSelect(template)}
                >
                  <div className="flex flex-col h-full">
                    <div className="mb-4">
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                        <template.icon className="h-6 w-6 text-primary" />
                      </div>
                      <h3 className="text-xl font-bold text-foreground mb-2">{template.name}</h3>
                      <p className="text-muted-foreground text-sm mb-4">{template.description}</p>
                    </div>

                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-foreground mb-2">Includes:</h4>
                      <ul className="space-y-2">
                        {template.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                            <span className="text-primary mt-0.5">✓</span>
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <Button
                      className="w-full mt-6 bg-primary hover:bg-primary/90"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTemplateSelect(template);
                      }}
                    >
                      Use This Template
                    </Button>
                  </div>
                </Card>
              ))}
            </div>

            <div className="mt-8 text-center">
              <Button
                variant="outline"
                onClick={() => window.location.href = '/chat-to-agent'}
                className="border-primary/50 hover:bg-primary/10"
              >
                <Sparkles className="h-4 w-4 mr-2" />
                Or Build Custom Agent with AI Chat
              </Button>
            </div>
          </div>
        )}

        {/* Step: Customize Template */}
        {step === 'customize' && selectedTemplate && (
          <div className="max-w-2xl mx-auto animate-fade-in">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <selectedTemplate.icon className="h-8 w-8 text-primary" />
              </div>
              <h1 className="text-3xl font-bold text-foreground mb-2">
                Customize Your {selectedTemplate.name} Agent
              </h1>
              <p className="text-muted-foreground">
                Personalize the template to match your business
              </p>
            </div>

            <Card className="p-6 bg-card border-border">
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="businessName">Business Name *</Label>
                    <Input
                      id="businessName"
                      placeholder="e.g., Acme Restaurant"
                      value={businessName}
                      onChange={(e) => setBusinessName(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="agentName">Agent Name</Label>
                    <Input
                      id="agentName"
                      placeholder="e.g., Sarah"
                      value={agentName}
                      onChange={(e) => setAgentName(e.target.value)}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Language</Label>
                    <Select value={language} onValueChange={setLanguage}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="en">English</SelectItem>
                        <SelectItem value="es">Spanish</SelectItem>
                        <SelectItem value="fr">French</SelectItem>
                        <SelectItem value="de">German</SelectItem>
                        <SelectItem value="he">Hebrew</SelectItem>
                        <SelectItem value="ar">Arabic</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Voice</Label>
                    <Select value={voiceGender} onValueChange={setVoiceGender}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="female">Female</SelectItem>
                        <SelectItem value="male">Male</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Tone</Label>
                    <Select value={tone} onValueChange={setTone}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="friendly">Friendly</SelectItem>
                        <SelectItem value="professional">Professional</SelectItem>
                        <SelectItem value="casual">Casual</SelectItem>
                        <SelectItem value="formal">Formal</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="additionalInstructions">Additional Instructions (Optional)</Label>
                  <Textarea
                    id="additionalInstructions"
                    placeholder="Any specific instructions for your agent..."
                    value={additionalInstructions}
                    onChange={(e) => setAdditionalInstructions(e.target.value)}
                    rows={4}
                  />
                </div>

                <div className="flex gap-4">
                  <Button
                    variant="outline"
                    onClick={() => setStep('select')}
                    className="flex-1"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handleCustomize}
                    disabled={!businessName}
                    className="flex-1 bg-primary hover:bg-primary/90"
                  >
                    <Phone className="h-4 w-4 mr-2" />
                    Create & Test Agent
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Step: Test */}
        {step === 'test' && agentConfig && (
          <TestOptions
            agentConfig={agentConfig}
            onRegister={(sid, aid) => {
              setSessionId(sid);
              setAgentId(aid);
              setStep('register');
            }}
            onBack={() => setStep('customize')}
          />
        )}

        {/* Step: Register */}
        {step === 'register' && (
          <RegistrationPrompt
            agentConfig={agentConfig}
            sessionId={sessionId}
            agentId={agentId}
            onComplete={() => {
              alert('Registration complete! Your agent has been saved.');
            }}
          />
        )}

        {/* Share Section */}
        <div className="max-w-4xl mx-auto mt-8">
          <SocialShareButtons />
        </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default TemplateToAgent;
